import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { BACKEND_URL, formatDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Upload, Send, Trash2, Download, Wrench } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { TIERS } from "@/data/content";

const STAGES = [
  "AWAITING_PAYMENT",
  "PAID_ACTIVATING",
  "BENCHMARK_SENT",
  "NEGOTIATION",
  "PDI_SCHEDULED",
  "PDI_DONE",
  "PAPERWORK",
  "DELIVERY_SCHEDULED",
  "DELIVERED",
  "CLOSED",
];

export default function AdminUserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ user: null, deal: null, documents: [], messages: [] });
  const [loading, setLoading] = useState(true);
  const [approveForm, setApproveForm] = useState({
    tier: "Arjuna",
    saarthi_name: "",
    saarthi_phone: "",
    car: "",
    city: "",
    payment_amount: 999,
    payment_mode: "UPI",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_reference: "",
  });
  const [msg, setMsg] = useState("");
  const [docTitle, setDocTitle] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setState(data);
      if (data.deal) {
        setApproveForm((f) => ({
          ...f,
          tier: data.deal.tier || data.deal.requested_tier || f.tier,
          saarthi_name: data.deal.saarthi_name || f.saarthi_name,
          saarthi_phone: data.deal.saarthi_phone || f.saarthi_phone,
          car: data.deal.car || f.car,
          city: data.deal.city || f.city,
        }));
      }
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }

  async function approve(e) {
    e.preventDefault();
    try {
      await api.post(`/admin/users/${id}/approve`, approveForm);
      toast.success("User approved. Dashboard unlocked.");
      load();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  }

  async function updateStage(stage) {
    try {
      await api.patch(`/admin/users/${id}/deal`, { stage });
      toast.success("Stage updated");
      load();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  }

  async function togglePDI(enabled) {
    try {
      await api.post(`/admin/users/${id}/pdi/${enabled ? "unlock" : "lock"}`);
      toast.success(enabled ? "PDI unlocked for buyer" : "PDI locked");
      load();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  }

  async function sendMessage(e) {
    e.preventDefault();
    if (!msg.trim()) return;
    try {
      await api.post(`/admin/users/${id}/messages`, { text: msg });
      setMsg("");
      load();
    } catch (err) { toast.error("Failed"); }
  }

  async function upload(e) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a file"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", docTitle || file.name);
      await api.post(`/admin/users/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setDocTitle("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  }

  async function deleteDoc(docId) {
    try {
      await api.delete(`/admin/users/${id}/documents/${docId}`);
      load();
    } catch { toast.error("Failed"); }
  }

  if (loading || !state.user) {
    return <main className="min-h-screen bg-void text-silver grid place-items-center"><p>Loading…</p></main>;
  }

  const { user, deal, documents, messages } = state;
  const paid = !!deal?.payment;

  return (
    <main className="min-h-screen bg-void text-silver" data-testid="admin-user-detail">
      <header className="border-b border-white/5 sticky top-0 bg-void/85 backdrop-blur-md z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <button onClick={() => nav("/admin/dashboard")} className="inline-flex items-center gap-2 text-silver/80 hover:text-white text-sm" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <Link to="/" className="flex items-center gap-2 opacity-70 hover:opacity-100">
            <span className="w-6 h-6 rounded-full gradient-bg" />
            <span className="font-serif text-lg text-white">CaarSaarthi</span>
          </Link>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 space-y-8">
        <div className="rounded-3xl border border-white/5 bg-surface p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="eyebrow gradient-text">User</p>
              <h1 className="mt-2 font-serif text-4xl text-white leading-tight" data-testid="user-name">{user.name}</h1>
              <p className="mt-1 text-silver/60 text-sm">{user.email} · {user.phone}</p>
              <p className="text-xs text-silver/40 mt-1">Joined {new Date(user.created_at).toLocaleString()}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {paid ? <Badge className="gradient-bg text-white border-0">{deal.tier} · Paid</Badge> : <Badge variant="outline" className="border-amber-500/40 text-amber-300">Awaiting payment</Badge>}
              {!paid && deal?.requested_tier && (
                <Badge variant="outline" className="border-[#8B5CF6]/40 text-[#C4B5FD]" data-testid="admin-requested-tier-badge">
                  Requested: {deal.requested_tier}
                </Badge>
              )}
              {deal?.stage && (
                <Select value={deal.stage} onValueChange={updateStage}>
                  <SelectTrigger className="bg-void border-white/10 h-9 text-white w-56" data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface text-silver border-white/10">
                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <label className="flex items-center gap-2 text-xs text-silver/70" data-testid="pdi-toggle">
                <Wrench className="w-3.5 h-3.5" />
                Remote PDI
                <Switch checked={!!deal?.pdi_unlocked} onCheckedChange={togglePDI} data-testid="pdi-toggle-switch" />
              </label>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <form onSubmit={approve} className="rounded-3xl gradient-border bg-[#141323] p-8 space-y-4" data-testid="approve-form">
            <p className="eyebrow gradient-text">Approve & Assign</p>
            <h3 className="font-serif text-2xl text-white">{paid ? "Update tier & payment" : "Unlock dashboard after payment"}</h3>

            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Tier">
                <Select value={approveForm.tier} onValueChange={(v) => { const t = TIERS.find(x => x.name === v); setApproveForm(f => ({ ...f, tier: v, payment_amount: t?.basePrice || f.payment_amount })); }}>
                  <SelectTrigger className="bg-void border-white/10 h-11 text-white" data-testid="approve-tier"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface text-silver border-white/10">
                    {TIERS.map(t => <SelectItem key={t.id} value={t.name}>{t.name} · {t.priceLabel}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Payment amount (₹)">
                <Input type="number" value={approveForm.payment_amount} onChange={(e) => setApproveForm(f => ({ ...f, payment_amount: Number(e.target.value) }))} className="bg-void border-white/10 h-11 text-white" data-testid="approve-amount" />
              </FormField>
              <FormField label="Payment mode">
                <Select value={approveForm.payment_mode} onValueChange={(v) => setApproveForm(f => ({ ...f, payment_mode: v }))}>
                  <SelectTrigger className="bg-void border-white/10 h-11 text-white" data-testid="approve-mode"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface text-silver border-white/10">
                    {["UPI","Bank","Card","Cash","Cheque"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Payment date">
                <Input type="date" value={approveForm.payment_date} onChange={(e) => setApproveForm(f => ({ ...f, payment_date: e.target.value }))} className="bg-void border-white/10 h-11 text-white" data-testid="approve-date" />
              </FormField>
              <FormField label="Payment reference">
                <Input value={approveForm.payment_reference} onChange={(e) => setApproveForm(f => ({ ...f, payment_reference: e.target.value }))} placeholder="UTR / txn id" className="bg-void border-white/10 h-11 text-white" data-testid="approve-ref" />
              </FormField>
              <FormField label="Assigned Saarthi">
                <Input value={approveForm.saarthi_name} onChange={(e) => setApproveForm(f => ({ ...f, saarthi_name: e.target.value }))} placeholder="Advisor name" className="bg-void border-white/10 h-11 text-white" data-testid="approve-saarthi-name" />
              </FormField>
              <FormField label="Saarthi phone">
                <Input value={approveForm.saarthi_phone} onChange={(e) => setApproveForm(f => ({ ...f, saarthi_phone: e.target.value }))} placeholder="+91 …" className="bg-void border-white/10 h-11 text-white" data-testid="approve-saarthi-phone" />
              </FormField>
              <FormField label="Car">
                <Input value={approveForm.car} onChange={(e) => setApproveForm(f => ({ ...f, car: e.target.value }))} className="bg-void border-white/10 h-11 text-white" data-testid="approve-car" />
              </FormField>
              <FormField label="City">
                <Input value={approveForm.city} onChange={(e) => setApproveForm(f => ({ ...f, city: e.target.value }))} className="bg-void border-white/10 h-11 text-white" data-testid="approve-city" />
              </FormField>
            </div>

            <button type="submit" className="w-full mt-4 py-3.5 rounded-full text-white gradient-bg hover:scale-[1.01] transition-transform text-sm font-medium" data-testid="approve-submit">
              {paid ? "Update tier & payment" : "Approve & unlock dashboard"}
            </button>
          </form>

          <div className="rounded-3xl border border-white/5 bg-surface p-8" data-testid="docs-panel">
            <p className="eyebrow gradient-text">Documents</p>
            <h3 className="mt-2 font-serif text-2xl text-white">Upload quotes, PDI reports, invoices</h3>
            <form onSubmit={upload} className="mt-6 space-y-3">
              <Input value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="Title (optional)" className="bg-void border-white/10 h-11 text-white" data-testid="doc-title" />
              <input ref={fileRef} type="file" className="block w-full text-sm text-silver file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-white/5 file:text-silver hover:file:bg-white/10" data-testid="doc-file" />
              <button type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm gradient-bg text-white hover:scale-[1.02] transition-transform" data-testid="doc-upload"><Upload className="w-4 h-4"/> Upload</button>
            </form>
            <ul className="mt-6 divide-y divide-white/5">
              {documents.length === 0 && <li className="py-4 text-silver/50 text-sm">No documents yet.</li>}
              {documents.map((d) => (
                <li key={d.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white truncate">{d.title || d.filename}</p>
                    <p className="text-xs text-silver/50">{(d.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const t = localStorage.getItem("cs_token");
                        fetch(`${BACKEND_URL}/api/admin/users/${id}/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${t}` } })
                          .then((r) => r.blob())
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url; a.download = d.filename; a.click();
                            URL.revokeObjectURL(url);
                          });
                      }}
                      className="p-2 rounded-full border border-white/10 hover:bg-white/5"
                      data-testid={`doc-download-${d.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button onClick={() => deleteDoc(d.id)} className="p-2 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4"/></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rounded-3xl border border-white/5 bg-surface p-8" data-testid="admin-messages-panel">
          <p className="eyebrow gradient-text">Conversation</p>
          <h3 className="mt-2 font-serif text-2xl text-white">Talk to {user.name}</h3>
          <div className="mt-6 max-h-80 overflow-auto space-y-3">
            {messages.length === 0 && <p className="text-silver/50 text-sm">No messages yet.</p>}
            {messages.map((m) => {
              const mine = m.from_role !== "user";
              const isSystem = m.from_role === "system";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${isSystem ? "bg-white/5 text-silver/70 italic" : mine ? "gradient-bg text-white" : "bg-void text-silver border border-white/10"}`}>
                    {!mine && <p className="text-[10px] uppercase tracking-widest text-silver/50 mb-1">{m.from_name}</p>}
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    <p className="mt-1 text-[10px] text-white/60">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={sendMessage} className="mt-4 flex gap-2">
            <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message the buyer…" className="bg-void border-white/10 text-white min-h-[52px]" data-testid="admin-msg-input" />
            <button type="submit" className="px-5 rounded-2xl gradient-bg text-white text-sm inline-flex items-center gap-2 hover:scale-[1.02] transition-transform" data-testid="admin-msg-send"><Send className="w-4 h-4"/> Send</button>
          </form>
        </div>
      </section>
    </main>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow gradient-text">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
